/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

import AbstractModule from "../abstract_module";
import IManipulatorModule from "./scene_manipulator_interface";
import {SurfaceColor, RenderDocument} from "js_renderer";
import {vec2, mat4, vec3} from "gl-matrix";
import IModel from "../../structural_elements/model_interface";
import BoundingBox from "../../elements/bounding_box";
import Tube from "../../elements/tube";
import Plane from "../../elements/plane";
import Curve from "../../elements/curve";
import Scene from "../../scene";

export type TPlane = {
    sapiElement: Plane,
    sapiOutlineElement: Curve,
    normal: vec3,
    positionIndices: number[]
}

export type TTubes = {
    sapiElement: Tube,
    normal: vec3
}

interface IManipulatorSettings {
}

export default
class ManipulatorModule extends AbstractModule implements IManipulatorModule {

    protected static _typeName: string = "manipulator";
    private _translation: vec3;
    private _yRotation: number;
    private _onlyMiddlePlanes: boolean;
    private _elementPickingAlreadyEnabled: boolean;
    private _object: IModel;
    private _planes: TPlane[];
    private _tubes: TTubes[];

    protected _defaultSettings: IManipulatorSettings;
    protected _settings: IManipulatorSettings;

    private _runTimeSettings: any = {};

    private _storedTransformation: mat4;

    constructor(aRenderDocument: RenderDocument) {
        super(aRenderDocument);

        this._type = ManipulatorModule._typeName;

        this._planes = []; // six planes for dragging the model
        this._tubes = [];

        this._runTimeSettings = {
            enabled: false,
            draggable: false,
            rotable: false,
            selectedIndex: -1,
            hoveredSapiElement: null,
            object: null, // could be scene api model or aggregation
            lastMousePosition: {
                x: null,
                y: null
            }
        };

        this._translation = vec3.create();
        this._yRotation = 0.0;

        this._onlyMiddlePlanes = true;
        this._elementPickingAlreadyEnabled = false;

        this._storedTransformation = null; // transofrmation written in DOM on the line "app1.addModel("geometryDescriptionFor_scene1", "modelDescriptionFor_scene1",here>>[1,0,0,0,0,1,0,0,0,0,1,0,0,0,0,1]<< here)"
    }

    public static getClassType(): string {
        return ManipulatorModule._typeName;
    }

    public attachObject(aObject: IModel) {
        this._object = aObject;
        this._object.applyManipulator();
    }

    public detachObject() {
        this._object.cancelManipulator();
        this._object = null;
    }

    public getStoredTransformation(): any {
        return this._storedTransformation;
    }

    private _createPlanes() {
        if (this._planes.length !== 0) {
            // TODO: update planes dimensions based on current bounding box for npc
            this._showPlanes();
            return;
        }

        let _sceneAPIBBox = this.sceneDocument.getSBoundingBoxForNPC();

        let normals;
        let colors;
        let positions;
        let positionIndices;

        if (!this._onlyMiddlePlanes) {
            normals = BoundingBox.getNormalsForAllPlanes();
            colors = BoundingBox.getColorsForAllPlanes();
            positions = BoundingBox.getPositionsForAllPlanes(_sceneAPIBBox.getCenter(), _sceneAPIBBox.getDimensions());
            positionIndices = BoundingBox.getIndicesForAllPlanes();
        } else {
            normals = BoundingBox.getNormalsForMiddlePlanes();
            colors = BoundingBox.getColorsForMiddlePlanes();
            positions = BoundingBox.getPositionsForMiddlePlanes(_sceneAPIBBox.getCenter(), _sceneAPIBBox.getDimensions());
            positionIndices = BoundingBox.getIndicesForMiddlePlanes();
        }

        for (let i = 0; i < normals.length; i++) {

            let sapiElement = <Plane>this.sceneDocument.createAPIElement(Plane.TypeName);
            this.sceneDocument.attachElementToInternalModel(sapiElement);

            let planePositions = [];
            for (let j = 0; j < 4; j++) {
                planePositions.push(positions[positionIndices[i][j] * 3 + 0]);
                planePositions.push(positions[positionIndices[i][j] * 3 + 1]);
                planePositions.push(positions[positionIndices[i][j] * 3 + 2]);
            }

            sapiElement.setPositions(new Float32Array(planePositions));
            sapiElement.extendProperties({
                clippable: false,
                contributeToNearPlaneClipping: false,
                transparency: 0.5,
                color: colors[i]
            });
            sapiElement.draw(true);

            let sapiOutlineElement = null;
            if (this._onlyMiddlePlanes) {
                let surfaceColor = SurfaceColor.getDefaultValues();
                surfaceColor.diffuseColor = colors[i];
                sapiOutlineElement = this.sceneDocument.createAPIElement(Curve.TypeName);
                this.sceneDocument.attachElementToInternalModel(sapiOutlineElement);
                sapiOutlineElement.setPositionsFromLineStripLoop(planePositions);
                sapiOutlineElement.extendProperties({
                    clippable: false,
                    contributeToNearPlaneClipping: false,
                    surfaceColor: surfaceColor
                });
                sapiOutlineElement.draw();
            }

            this._planes.push({
                sapiElement: sapiElement,
                sapiOutlineElement: sapiOutlineElement,
                normal: normals[i],
                positionIndices: positionIndices[i]
            });
        }
    }

    private _createTubes() {
        if (this._tubes.length !== 0) {
            this._showTubes();
            return;
        }

        let normals = [
            vec3.fromValues(0, 1, 0)
        ];

        let colors = [
            vec3.fromValues(0, 1, 0)
        ];

        let _sceneAPIBBox = this.sceneDocument.getSBoundingBoxForNPC();
        let bboxDims = _sceneAPIBBox.getDimensions();

        for (let i = 0; i < normals.length; i++) {

            let sapiElement = <Tube>this.sceneDocument.createAPIElement(Tube.TypeName);
            this.sceneDocument.attachElementToInternalModel(sapiElement);

            sapiElement.setPositions(Tube.getAllPositions(
                _sceneAPIBBox.getCenter(),
                bboxDims[1] * 1.5,
                Math.max(bboxDims[0], bboxDims[1]) / 10.0,
                10));
            sapiElement.extendProperties({clippable: false, transparency: 0.2, color: colors[i]});
            sapiElement.draw(true);

            this._tubes.push({
                sapiElement: sapiElement,
                normal: normals[i]
            });
        }
    }

    private _showPlanes() {
        this._updatePlanePositions();

        for (let i = 0; i < this._planes.length; i++) {
            this._planes[i].sapiElement.setVisibility(true);
            if (this._planes[i].sapiOutlineElement) {
                this._planes[i].sapiOutlineElement.setVisibility(true);
            }
        }

        this.sceneAPI.viewModeModule.scheduleRedraw();
    }

    private _hidePlanes() {
        for (let i = 0; i < this._planes.length; i++) {
            this._planes[i].sapiElement.setVisibility(false);
            if (this._planes[i].sapiOutlineElement) {
                this._planes[i].sapiOutlineElement.setVisibility(false);
            }
        }

        this.sceneAPI.viewModeModule.scheduleRedraw();
    }

    private _showTubes() {
        this._updateTubePositions();

        for (let i = 0; i < this._tubes.length; i++) {
            this._tubes[i].sapiElement.setVisibility(true);
        }

        this.sceneAPI.viewModeModule.scheduleRedraw();
    }

    private _hideTubes() {
        for (let i = 0; i < this._tubes.length; i++) {
            this._tubes[i].sapiElement.setVisibility(false);
        }

        this.sceneAPI.viewModeModule.scheduleRedraw();
    }

    public isEnabled(): boolean {
        return this._runTimeSettings.enabled;
    }

    public setEnabled(aValue: boolean) {
        if (aValue && !this._runTimeSettings.enabled) {
            this.reset();
            this._createPlanes();
            this._createTubes();

            let view = this.sceneAPI.viewModeModule.getActiveView();
            if (view) {
                view.registerUserInputObserver(this);
            }

            this._elementPickingAlreadyEnabled = !this.sceneAPI.pickingModule.startElementPicking({});
        } else if (!aValue && this._runTimeSettings.enabled) {

            this._hidePlanes();
            this._hideTubes();

            let view = this.sceneAPI.viewModeModule.getActiveView();
            if (view) {
                view.unregisterUserInputObserver(this);
            }

            this.bake();

            // reset the translation and rotation
            this._translation = vec3.create();
            this._yRotation = 0.0;

            if (!this._elementPickingAlreadyEnabled) {
                this.sceneAPI.pickingModule.stopElementPicking();
            }
        }

        this._runTimeSettings.enabled = aValue;

        let webglRenderGraph = this.renderDocument.getRenderer().getWebGLRenderGraph();
        if (webglRenderGraph) {
            webglRenderGraph.initializeShaders();
        }
    }

    private _updatePlanePositions() {
        let _sceneAPIBBox = this.sceneDocument.getSBoundingBoxForNPC();
        let center = _sceneAPIBBox.getCenter();

        vec3.add(center, center, this._translation);

        let positions;
        if (this._onlyMiddlePlanes) {
            positions = BoundingBox.getPositionsForMiddlePlanes(center, _sceneAPIBBox.getDimensions());
        } else {
            positions = BoundingBox.getPositionsForAllPlanes(center, _sceneAPIBBox.getDimensions());
        }

        for (let i = 0; i < this._planes.length; i++) {
            let planePositions = [];
            for (let j = 0; j < 4; j++) {
                planePositions.push(positions[this._planes[i].positionIndices[j] * 3 + 0]);
                planePositions.push(positions[this._planes[i].positionIndices[j] * 3 + 1]);
                planePositions.push(positions[this._planes[i].positionIndices[j] * 3 + 2]);
            }

            this._planes[i].sapiElement.setPositions(new Float32Array(planePositions));
            if (this._planes[i].sapiOutlineElement) {
                this._planes[i].sapiOutlineElement.setPositionsFromLineStripLoop(planePositions);
            }
        }
    }

    private _updateTubePositions() {
        let _sceneAPIBBox = this.sceneDocument.getSBoundingBoxForNPC();
        let center = _sceneAPIBBox.getCenter();

        vec3.add(center, center, this._translation);

        let bboxDims = _sceneAPIBBox.getDimensions();
        for (let i = 0; i < this._tubes.length; i++) {

            let positions = Tube.getAllPositions(
                center,
                bboxDims[1] * 1.5,
                Math.max(bboxDims[0], bboxDims[1]) / 10.0,
                10
            );

            this._tubes[i].sapiElement.setPositions(positions);
        }
    }

    public onMousePress(x: number, y: number, buttonState: number) {
        if (!this._runTimeSettings.enabled) {
            return;
        }

        this._runTimeSettings.selectedIndex = -1;

        let sapielement = this.sceneAPI.pickingModule.pickSAPIElement();
        for (let i = 0; i < this._planes.length; i++) {
            if (sapielement === this._planes[i].sapiElement) {
                this._runTimeSettings.draggable = true;
                this._runTimeSettings.rotable = false;
                //this.sceneAPI.viewModule._getActiveView().getViewControls().enable(false);
                this.sceneAPI.pickingModule.disableCameraRotation(true);

                this._runTimeSettings.selectedIndex = i;
                return;
            }
        }

        for (let i = 0; i < this._tubes.length; i++) {
            if (sapielement === this._tubes[i].sapiElement) {
                this._runTimeSettings.rotable = true;
                this._runTimeSettings.draggable = false;
                //this.sceneAPI.viewModule._getActiveView().getViewControls().enable(false);
                this.sceneAPI.pickingModule.disableCameraRotation(true);

                this._runTimeSettings.selectedIndex = i;

                return;
            }
        }
    }

    public onTouchStart(x: number, y: number, numTouches: number) {
        this.onMousePress(x, y, 0);
    }

    public onMouseRelease(x: number, y: number, buttonState: number) {
        if (!this._runTimeSettings.enabled) {
            return;
        }

        if (this._runTimeSettings.draggable || this._runTimeSettings.rotable) {
            this._runTimeSettings.draggable = false;
            this._runTimeSettings.rotable = false;

            //this.sceneAPI.viewModule._getActiveView().getViewControls().enable(true);
            this.sceneAPI.pickingModule.disableCameraRotation(false);

            this._runTimeSettings.lastMousePosition.x = null;
            this._runTimeSettings.lastMousePosition.y = null;
        }
    }

    public onTouchEnd(x: number, y: number, numTouches: number) {
        this.onMouseRelease(x, y, 0);
    }

    public onMove(x: number, y: number, delta: number, buttonState: number) {
        let sapielement = this.sceneAPI.pickingModule.pickSAPIElement();

        if (this._runTimeSettings.hoveredSapiElement && this._runTimeSettings.hoveredSapiElement !== sapielement) {
            this._runTimeSettings.hoveredSapiElement.unselect();
        }

        for (let i = 0; i < this._planes.length; i++) {
            if (sapielement === this._planes[i].sapiElement) {
                this._runTimeSettings.hoveredSapiElement = sapielement;
                sapielement.select(true);
                return;
            }
        }

        for (let i = 0; i < this._tubes.length; i++) {
            if (sapielement === this._tubes[i].sapiElement) {
                this._runTimeSettings.hoveredSapiElement = sapielement;
                sapielement.select(true);
                return;
            }
        }
    }

    private _performDrag(x: number, y: number, delta: number, buttonState: number) {
        let view = this.sceneAPI.viewModeModule.getActiveView();

        // we should project the plane normal to screen and compute angle between it and the drag vector
        let _sceneAPIBBox = this.sceneDocument.getSBoundingBoxForNPC();
        let nStartPoint3d = _sceneAPIBBox.getCenter();
        let nEndPoint3d = vec3.create();
        vec3.add(nEndPoint3d, nStartPoint3d, this._planes[this._runTimeSettings.selectedIndex].normal);

        let nStartPoint2d = vec2.clone(view.projectPointToWindow(nStartPoint3d));
        let nEndPoint2d = vec2.clone(view.projectPointToWindow(nEndPoint3d));

        let normalVec2d = vec2.fromValues(nEndPoint2d[0] - nStartPoint2d[0], nEndPoint2d[1] - nStartPoint2d[1]);
        let dragVec2d = vec2.fromValues((x - this._runTimeSettings.lastMousePosition.x), (y - this._runTimeSettings.lastMousePosition.y));

        let step = vec2.length(dragVec2d) / vec2.length(normalVec2d);

        vec2.normalize(normalVec2d, normalVec2d);
        vec2.normalize(dragVec2d, dragVec2d);

        let angle = Math.acos(vec2.dot(normalVec2d, dragVec2d));
        if (angle < (Math.PI / 2.0)) {
            vec3.scaleAndAdd(this._translation, this._translation, this._planes[this._runTimeSettings.selectedIndex].normal, step);
        } else {
            vec3.scaleAndAdd(this._translation, this._translation, this._planes[this._runTimeSettings.selectedIndex].normal, -step);
        }
    }

    private _performRotation(x: number, y: number, delta: number, buttonState: number) {
        let view = this.sceneAPI.viewModeModule.getActiveView();

        // we should project the plane normal to screen and compute angle between it and the drag vector
        let _sceneAPIBBox = this.sceneDocument.getSBoundingBoxForNPC();
        let nStartPoint3d = _sceneAPIBBox.getCenter();
        let nEndPoint3d = vec3.create();
        vec3.add(nEndPoint3d, nStartPoint3d, vec3.fromValues(1, 0, 0));

        let nStartPoint2d = vec3.clone(view.projectPointToWindow(nStartPoint3d));
        let nEndPoint2d = vec3.clone(view.projectPointToWindow(nEndPoint3d));

        let normalVec2d = vec2.fromValues(nEndPoint2d[0] - nStartPoint2d[0], nEndPoint2d[1] - nStartPoint2d[1]);
        let dragVec2d = vec2.fromValues((x - this._runTimeSettings.lastMousePosition.x), (y - this._runTimeSettings.lastMousePosition.y));

        let step = vec2.length(dragVec2d) / vec2.length(normalVec2d);
        step = step / 8.0; // slow down constant;

        if (dragVec2d[0] > 0) {
            this._yRotation += step;
            this.sceneAPI.clippingPlaneModule.addYRotationOffset(step);
        } else {
            this._yRotation -= step;
            this.sceneAPI.clippingPlaneModule.addYRotationOffset(-step);
        }
    }

    public onDrag(x: number, y: number, delta: number, buttonState: number) {
        if (!this.sceneAPI.viewModeModule.getActiveView().getControls().isRotating(buttonState)) {
            return;
        }

        if (this._runTimeSettings.draggable || this._runTimeSettings.rotable) {
            if (this._runTimeSettings.lastMousePosition.x === null) {
                this._runTimeSettings.lastMousePosition.x = x;
                this._runTimeSettings.lastMousePosition.y = y;
                return;
            }

            if (this._runTimeSettings.draggable) {
                this._performDrag(x, y, delta, buttonState);
            } else if (this._runTimeSettings.rotable) {
                this._performRotation(x, y, delta, buttonState);
            }

            this.sceneAPI.activityModule.setCurrentManipulatorTransformation(this._computeTransformation());

            this._runTimeSettings.lastMousePosition.x = x;
            this._runTimeSettings.lastMousePosition.y = y;

            this._updatePlanePositions();
            this._updateTubePositions();
        }
    }

    public onTouchMove(x: number, y: number, delta: number, buttonState: number) {
        this.onDrag(x, y, 0, 1);
    }

    private _computeTransformation() {
        // get bounding box from visible geometry objects in the active object
        if (!this._object) {
            return mat4.create();
        }

        let aggregationSAPIBBox = this._object.getSBoundingBoxFromVisibleObjectsForNPC();

        if (!aggregationSAPIBBox.isValid()) {
            return mat4.create();
        }

        // center of rotation is center point of bottom side of the bounding box
        let centerOfRotation = aggregationSAPIBBox.getCenter();
        centerOfRotation[1] = aggregationSAPIBBox.getMin()[1];

        // 1. translate the model to center of rotation
        let translateToRotationCenterMat = mat4.create();
        mat4.translate(translateToRotationCenterMat, translateToRotationCenterMat, centerOfRotation);

        // 2. translate to the translation from this manipulator
        let manipulatorTranslationMat = mat4.create();
        let translationVecVS = this._translation;
        mat4.translate(manipulatorTranslationMat, manipulatorTranslationMat, translationVecVS);

        // 3. rotate the model around y axis
        let rotationMat = mat4.create();
        mat4.rotate(rotationMat, rotationMat, this._yRotation, vec3.fromValues(0.0, 1.0, 0.0));

        // 4. translate the model back to its position
        let translateToModelCenterMat = mat4.create();
        mat4.invert(translateToModelCenterMat, translateToRotationCenterMat);

        // apply the transformations
        let transformation = mat4.create();
        mat4.multiply(transformation, transformation, manipulatorTranslationMat);
        mat4.multiply(transformation, transformation, translateToRotationCenterMat);
        mat4.multiply(transformation, transformation, rotationMat);
        mat4.multiply(transformation, transformation, translateToModelCenterMat);

        return transformation;
    }

    public rotateAroundYAxis(aAngleRad: number) {
        this._yRotation = aAngleRad;

        //send the transformation of the manipulator to the activity module
        this.sceneAPI.activityModule.setCurrentManipulatorTransformation(this._computeTransformation());
    }

    /***
     * The bake method should take the translation and store it in DOM as model transformation at load. The y rotation
     * should be used for y angle rotation for the zeropoint because we need to keep the local coordinates of the model as they are
     */
    public bake() {
        if (!this._object) {
            return;
        }

        this._object.transform(this.sceneAPI.activityModule.getCurrentManipulatorTransformation());

        // this is HACK and should not be here. Correct solution is to assign the tranformation to appropriate transformed objects
        // which can be time consuming task as we need to search for the objects in DOM and change the DOM.
        // So instead we assign the transformation to added model (we expect that the _object here is render model)
        this._saveToDOM();

        // reset the manipulator
        this.reset();

        // recompute shadow volumes if we have some
        this.sceneAPI.shadowsModule.recompute();
    }

    public reset() {
        this._runTimeSettings.selectedIndex = -1;
        this._runTimeSettings.hoveredSapiElement = null;
        this._runTimeSettings.lastMousePosition.x = null;
        this._runTimeSettings.lastMousePosition.y = null;

        this._yRotation = 0.0;
        this._translation = vec3.create();

        this.sceneAPI.activityModule.setCurrentManipulatorTransformation(this._computeTransformation());
    }

    /***
     * We save the manipulator transformation to DOM by adding a model.transform([]) line to
     * the model loading script. This is a bit kind of hack, but as there is no concept of saving the model yet, we will
     * do it like that for now. Better solution should be to bake the manipulator result to the affected models and resave
     * the geometry stored in DOM. But this can potentially take a lot of time.
     */
    private _saveToDOM() {
        let currTrafo = this.sceneAPI.activityModule.getCurrentManipulatorTransformation();
        if (currTrafo) {
            let script = document.getElementById('mainScript');
            if (script) {
                // look if some transformation is already included here
                let lines = script.innerHTML.split("\n");

                for (let l = 0; l < lines.length; l++) {
                    let line = lines[l];

                    let searchString = "<<ADD_LOCAL_MODEL_FOR_SAVE_TO_DOM_MAGIC_STRING>>";
                    let index = line.indexOf(searchString);
                    if (index !== -1) {
                        // search for string addModel
                        index = line.indexOf("app1.addModel(\"");
                        if (index !== -1) {
                            let searchTrafoBeginString = "[";
                            let trafoBeginIndex = line.indexOf(searchTrafoBeginString);
                            if (trafoBeginIndex !== -1) {
                                let searchTrafoEndString = "]);";
                                let trafoEndIndex = line.indexOf(searchTrafoEndString);
                                let trafoString = line.substr(trafoBeginIndex, trafoEndIndex - trafoBeginIndex);
                                trafoString += "]";

                                let parsedTrafo = mat4.create();
                                parsedTrafo = JSON.parse(trafoString);

                                // this solves the case that there is already some trafo written in the html
                                this._storedTransformation = mat4.create();
                                mat4.multiply(this._storedTransformation, currTrafo, parsedTrafo);
                                lines[l] = line.substr(0, trafoBeginIndex) + "[" + this._storedTransformation.toString() + line.substr(trafoEndIndex);
                            } else {
                                this._storedTransformation = currTrafo;
                                // this solves the case that there is no trafo written in the html yet
                                trafoBeginIndex = line.indexOf(");");
                                lines[l] = line.substr(0, trafoBeginIndex) + ",[" + currTrafo.toString() + "]" + line.substr(trafoBeginIndex);
                            }

                            script.innerHTML = "";
                            for (let m = 0; m < lines.length; m++) {
                                script.innerHTML += lines[m] + "\n";
                            }

                            return;
                        }
                    }
                }
            }
        }
    }

    public viewChanged() {
    }

    public viewResized(aContentSize) {
    }
}

Scene.registerSceneAPIModule(ManipulatorModule);
